import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { cx } from './cx.js';
import { fieldLook } from './TextInput.js';

/**
 * The one dropdown.
 *
 * A closed field that opens a menu the brand drew. It used to be a native
 * `<select>` with its own arrow turned off and ours painted over it, on the
 * argument that the menu it opens is the operating system's and the operating
 * system knows how to make a menu usable on a phone. That argument is still
 * true about the *menu*, and it is the whole reason this was left alone for so
 * long — but the menu is the part a player looks at while choosing, and on
 * macOS it is a dark gray slab in the system font landing in the middle of a
 * sunbleached porcelain page. Every other control in the app is a white chip
 * with a 2px ink stroke. The one that is not is the one that opens.
 *
 * So the popup is ours now, and the cost is real: everything a `<select>` gave
 * away for free has to be written out here, once, correctly — the roles, the
 * keyboard, the typeahead, the outside press, and where the panel goes when
 * there is no room below it. That is the trade this component exists to make.
 * Nothing else in the app is allowed to make it again.
 *
 * **The shape is the APG select-only combobox**, not a menu. A `role="menu"`
 * announces commands; this announces a value that is currently chosen and a
 * list of the others, which is a `listbox`. Focus never leaves the trigger:
 * the rows are not tab stops, and the trigger names the active one with
 * `aria-activedescendant`. That is what makes it safe inside `Sheet`, whose
 * focus trap would otherwise fight a panel portalled to `document.body`.
 *
 * **The panel is `position: fixed` in a portal**, and both halves are load
 * bearing. The settings sheet's body is `overflow-y-auto`, so a panel drawn as
 * an absolute child of the field would be clipped by it the moment the field
 * sat near the bottom of the drawer. Fixed to the viewport, measured off the
 * trigger, re-measured on scroll and resize.
 *
 * @see `apps/client/src/ui/Choice.tsx` for the settings-row wrapper that gives
 * one a real name.
 */

export interface SelectOption<V extends string = string> {
  readonly value: V;
  /** Already translated. This is what the row reads and what typeahead matches. */
  readonly label: string;
  readonly disabled?: boolean | undefined;
}

/**
 * `V` is the caller's own union of values. Options, value and change share it,
 * so a screen never casts `next` back to its union and an option outside it does
 * not compile. It defaults to `string` for a caller whose values are open.
 */
export interface SelectProps<V extends string = string> {
  /** Data, not `<option>` children: the panel has to render the list itself. */
  readonly options: readonly SelectOption<V>[];
  readonly value: V;
  readonly onChange: (next: V) => void;
  /** Fill the row it sits in. Off for a control parked beside a label. */
  readonly fullWidth?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  /** What the closed field reads when `value` matches no option. */
  readonly placeholder?: string | undefined;
  /** Goes on the wrapper, because the wrapper is what the layout sees. */
  readonly className?: string | undefined;
  readonly 'aria-label'?: string | undefined;
  /**
   * The id of the visible label, and the preferred way to name one.
   *
   * A `<label for>` cannot name this the way it named the old `<select>`: the
   * name of a combobox is the label and its *value* is its content, so this is
   * wired as `aria-labelledby="<label> <value>"` and a reader announces both.
   * Point it at a real element or pass `aria-label`; a dropdown that announces
   * as nothing at all is the failure this replaces.
   */
  readonly 'aria-labelledby'?: string | undefined;
  readonly 'aria-describedby'?: string | undefined;
  readonly 'aria-invalid'?: boolean | undefined;
}

/*
 * The field look is shared with every text input, so a select and an input
 * beside it cannot drift apart. Its focus pair is documented at `fieldLook` —
 * read that before removing either half. Half of it was removed once already,
 * on a theory the browser does not support, and a Tab-focused select came back
 * reporting `outline-style: none`.
 *
 * What is added here is the row: the value on the left, the chevron parked on
 * the right, and `text-left` because a `<button>` centers its content and a
 * field does not. The old `pr-[2.6rem]` is gone with the native arrow it was
 * making room for — the chevron is a flex sibling now and reserves its own.
 *
 * Disabled is the `Button` treatment, deliberately: outline to `gray-300`,
 * text to `gray-400`, still legible. A control you cannot read is a control you
 * have lost. It is written here rather than into `fieldLook`, because a
 * disabled text input has a different problem — it still has to show what was
 * typed into it — and that is a decision to take on its own.
 */
const TRIGGER = cx(
  fieldLook,
  'flex cursor-pointer items-center gap-2 text-left font-medium',
  'disabled:cursor-default disabled:border-gray-300 disabled:text-gray-400',
);

/*
 * A chip at menu size: the same white face, 2px ink stroke and hard offset
 * shadow every raised surface in the brand wears. `z-70` clears `Sheet`'s
 * `z-60`, because the commonest dropdown in the app is inside that drawer.
 *
 * `gap-0.5` holds the rows apart because two of them can be filled at once —
 * lemon on the answer, gray on the active row — and the first arrow press
 * always lands them side by side. Touching, the two fills fused into one
 * notched blob; apart, each reads as its own chip.
 *
 * The animation is a settle and not a bounce, per BRAND: 120ms of opacity and
 * a hair of scale from the top edge, so the panel reads as unfolding out of the
 * field rather than arriving from somewhere else. `motion-reduce` turns it off.
 */
const PANEL =
  'pointer-events-auto flex flex-col gap-0.5 overflow-y-auto overscroll-contain rounded-2xl border-2 border-ink bg-ground-light p-1 shadow-sm shadow-ink select-panel motion-reduce:animate-none';

const ROW =
  'flex min-h-12 cursor-pointer items-center gap-2 rounded-xl px-3 py-2 leading-snug transition-control duration-150 ease-out';

/**
 * The row states, and how they are kept apart.
 *
 * Selected and active used to share one channel. Both painted the background
 * and selected won, so arrowing onto a different row lit a seven percent wash
 * while the answer already given kept a full ink field: the loudest thing in
 * the panel was a record of the past, and the quietest was what Enter would
 * commit.
 *
 * They are far enough apart now to share it safely. **Selected is lemon**, with
 * the book weight and a check beside the label — three channels saying one
 * thing, so the row survives a colorblind reader and a high-contrast theme.
 * **Active is `gray-300`**, the hairline stop spent as a fill: plainly visible
 * on a white panel and nowhere near lemon, which is exactly what the old
 * `ground-dark` recess was not. Selected wins where the two meet — every time
 * the panel opens on its own value — because dropping a gray wash over the
 * current answer is a downgrade and not a highlight.
 *
 * Lemon on the chosen row is a deliberate exception to BRAND rule 2, taken
 * 2026-08-27, and written into the rule rather than around it: rule 2 is aimed
 * at eight switches sitting at rest on one settings screen, and a menu holds
 * one selected row and holds it only while it is open. `lemon-muted` is the
 * obvious softer field and is wrong here — the wash is mixed to sit on
 * `--ground`, and on the panel's `ground-light` face it lands four percent off
 * white.
 */
function rowLook(selected: boolean, active: boolean, disabled: boolean): string {
  if (disabled) return cx(ROW, 'cursor-default text-gray-400');
  if (selected) return cx(ROW, 'bg-lemon font-bold text-ink');
  return cx(ROW, 'text-ink', active && 'bg-gray-300');
}

/**
 * Where the panel goes. Fixed coordinates, measured off the trigger.
 *
 * `width` is the field's, and it is the panel's minimum rather than its width.
 * A row carries the panel's stroke and padding, its own padding and the
 * check's box, none of which the closed field carries around its value, so a
 * label that fits the field on one line wrapped onto two in its own row
 * ("Two players", 2026-09-04, once the check took the caret's 20-point box).
 * The panel grows to its longest row instead, as a menu does, and `maxWidth`
 * keeps it inside the viewport; a full-width field still gets a panel exactly
 * its width, because nothing in it is wider.
 */
interface Placement {
  readonly left: number;
  readonly width: number;
  readonly maxWidth: number;
  readonly maxHeight: number;
  /** One of the two is set. Anchoring by `bottom` is what lets it open upward. */
  readonly top?: number | undefined;
  readonly bottom?: number | undefined;
}

/** The gap between field and panel, and the panel's clearance from the edge. */
const GAP = 6;
const MARGIN = 8;
/** Taller than this and a menu stops being a menu. About six rows. */
const TALLEST = 320;
/** Under this much room below, the panel would rather open upward. */
const CRAMPED = 160;

function measure(anchor: HTMLElement | null): Placement | null {
  if (anchor === null) return null;
  const box = anchor.getBoundingClientRect();
  const below = window.innerHeight - box.bottom - GAP - MARGIN;
  const above = box.top - GAP - MARGIN;
  const upward = below < CRAMPED && above > below;
  const room = Math.max(upward ? above : below, CRAMPED);
  const place = {
    left: box.left,
    width: box.width,
    maxWidth: window.innerWidth - box.left - MARGIN,
    maxHeight: Math.min(TALLEST, room),
  };
  return upward
    ? { ...place, bottom: window.innerHeight - box.top + GAP }
    : { ...place, top: box.bottom + GAP };
}

/** The next row the arrow keys are allowed to land on, or where they started. */
function step(options: readonly SelectOption[], from: number, delta: number): number {
  for (let index = from + delta; index >= 0 && index < options.length; index += delta) {
    if (options[index]?.disabled !== true) return index;
  }
  return from;
}

/** The first or last row the keyboard may land on, for Home and End. */
function edge(options: readonly SelectOption[], delta: number): number {
  const from = delta > 0 ? -1 : options.length;
  return step(options, from, delta);
}

/** How long a typeahead keeps collecting letters before it starts a new word. */
const TYPING = 600;

export function Select<V extends string = string>({
  options,
  value,
  onChange,
  fullWidth = true,
  disabled = false,
  placeholder,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': labelledBy,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: SelectProps<V>): React.JSX.Element {
  const ids = useId();
  const listId = `${ids}list`;
  const valueId = `${ids}value`;
  const rowId = (index: number): string => `${ids}row${index}`;

  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: '', at: 0 });

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<Placement | null>(null);
  const [panelBox, setPanelBox] = useState<{ width: number; height: number } | null>(null);

  // Measure the intrinsic HTML menu before committing its SVG viewport. The
  // second pass measures wrapping at the trigger's minimum width. This keeps
  // placement independent of CSS anchor-positioning browser support.
  useLayoutEffect(() => {
    if (!open || place === null || panel.current === null) return;
    const element = panel.current;
    const width = Math.min(place.maxWidth, Math.max(place.width, element.offsetWidth));
    const height = Math.min(place.maxHeight, element.scrollHeight + 4);
    setPanelBox((before) =>
      before?.width === width && before.height === height ? before : { width, height },
    );
  }, [open, place, panelBox?.width]);

  const chosen = options.findIndex((option) => option.value === value);
  /* A placeholder is not a value, and it reads as `gray-700` below for the same
     reason `TextInput`'s does: dimmer than an answer, and still above the AA
     floor on the field's white face. */
  const shown = chosen >= 0 ? options[chosen]?.label : placeholder;

  const show = useCallback(() => {
    if (disabled) return;
    // Safari does not focus buttons on a pointer click. The combobox must own
    // focus while aria-activedescendant identifies the active option.
    trigger.current?.focus();
    setActive(chosen >= 0 ? chosen : edge(options, 1));
    setPanelBox(null);
    setPlace(measure(trigger.current));
    setOpen(true);
  }, [chosen, disabled, options]);

  const commit = useCallback(
    (index: number) => {
      const option = options[index];
      setOpen(false);
      trigger.current?.focus();
      if (option === undefined || option.disabled === true) return;
      if (option.value !== value) onChange(option.value);
    },
    [onChange, options, value],
  );

  /* The panel is fixed to the viewport, so anything that moves the field under
     it has to move it too. Capture phase: the scroller is usually an ancestor
     — the settings sheet's body — and a scroll event does not bubble. */
  useEffect(() => {
    if (!open) return undefined;
    const follow = (): void => {
      setPanelBox(null);
      setPlace(measure(trigger.current));
    };
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  /* A press anywhere else puts it away. `pointerdown` rather than `click`, so
     the panel is gone before the press that dismissed it lands on whatever is
     underneath — and in capture, so a handler that stops propagation on its own
     subtree cannot leave the menu hanging open over the app. */
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (trigger.current?.contains(target) === true) return;
      if (panel.current?.contains(target) === true) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', away, true);
    return () => {
      document.removeEventListener('pointerdown', away, true);
    };
  }, [open]);

  /* Arrowing past the fold has to bring the row with it. `block: 'nearest'`
     scrolls the panel only when the row is actually outside it, which is what
     keeps a mouse hover from yanking the list around. Guarded because jsdom
     does not implement the method and the unit suite runs there. */
  useEffect(() => {
    if (!open) return;
    const row = panel.current?.querySelector<HTMLElement>('[data-active="true"]');
    if (row === undefined || row === null) return;
    if (typeof row.scrollIntoView !== 'function') return;
    row.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  /**
   * Jump to the row a player is spelling.
   *
   * The same affordance a native `<select>` has, and the one nobody remembers
   * to write: in a list of 250 countries, "n-o-r" is the difference between a
   * usable control and a scroll bar. Letters collect for `TYPING` and then
   * start a new word, and the search begins after the active row so pressing
   * one letter twice walks the rows that start with it.
   */
  const typeahead = (key: string): boolean => {
    const now = Date.now();
    const fresh = now - typed.current.at > TYPING;
    const text = (fresh ? '' : typed.current.text) + key.toLowerCase();
    typed.current = { text, at: now };

    const from = text.length === 1 ? active + 1 : active;
    for (let offset = 0; offset < options.length; offset += 1) {
      const index = (from + offset + options.length) % options.length;
      const option = options[index];
      if (option === undefined || option.disabled === true) continue;
      if (!option.label.toLowerCase().startsWith(text)) continue;
      if (open) setActive(index);
      else commit(index);
      return true;
    }
    return false;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (disabled) return;
    const { key } = event;
    const typing = key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;

    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        show();
      } else if (typing && key !== ' ' && typeahead(key)) {
        event.preventDefault();
      }
      return;
    }

    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive(step(options, active, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive(step(options, active, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActive(edge(options, 1));
        break;
      case 'End':
        event.preventDefault();
        setActive(edge(options, -1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(active);
        break;
      /* Escape stops here. `Sheet` closes on an Escape that reaches it, and
         the commonest dropdown in the app is inside that drawer — one press
         would have put the menu away and the whole settings sheet with it. A
         key that had a local effect is not the page's to act on. */
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        break;
      /* Tab leaves without choosing, and is deliberately not prevented: the
         focus goes on to the next control, which is what Tab is for. */
      case 'Tab':
        setOpen(false);
        break;
      default:
        if (typing && typeahead(key)) event.preventDefault();
    }
  };

  return (
    <span className={cx('relative block', fullWidth && 'w-full', className)}>
      <button
        ref={trigger}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? rowId(active) : undefined}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy === undefined ? undefined : `${labelledBy} ${valueId}`}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        disabled={disabled}
        className={TRIGGER}
        onClick={() => {
          if (open) setOpen(false);
          else show();
        }}
        onKeyDown={onKeyDown}
      >
        {/* The value is the button's content, which is what a reader announces
            as the combobox's value once the label has given it a name. */}
        <span id={valueId} className={cx('min-w-0 flex-1 truncate', chosen < 0 && 'text-gray-700')}>
          {shown}
        </span>
        {/* Decoration: no pointer target, no accessible name, out of the tab
            order. It turns over when the panel is open, which is the one thing
            the closed field can say about the open one.

            Bold, in a 20-point box. A Phosphor glyph fills about seven tenths
            of its box, so this draws the caret itself about 14 wide, 8 tall
            and 1.9 thick: the 12-by-8, 2-point chevron the field wore before
            the icon set came in. At the regular weight in an 8-by-12 box it
            was a hairline, 0.75 thick, which Mitch read as too small on
            2026-09-04. `-mr-0.5` gives back most of the box's own padding, so
            the caret's edge sits inside the field's padding by about the side
            bearing the value's last letter keeps on the left. */}
        <CaretDownIcon
          weight="bold"
          className={cx(
            '-mr-0.5 h-5 w-5 shrink-0 transition-transform duration-150 ease-out',
            open && 'rotate-180',
          )}
          aria-hidden="true"
          focusable="false"
        />
      </button>

      {open &&
        place !== null &&
        createPortal(
          <svg
            className="pointer-events-none fixed inset-0 z-70 size-full overflow-visible"
            role="presentation"
            focusable="false"
          >
            <foreignObject
              x={place.left}
              y={
                place.top ??
                window.innerHeight - (place.bottom ?? 0) - (panelBox?.height ?? place.maxHeight)
              }
              width={panelBox?.width ?? place.maxWidth}
              height={panelBox?.height ?? place.maxHeight}
              className="overflow-visible"
            >
              <div
                ref={panel}
                id={listId}
                role="listbox"
                aria-label={ariaLabel}
                aria-labelledby={labelledBy}
                className={cx(
                  PANEL,
                  place.top === undefined ? 'origin-bottom' : 'origin-top',
                  'max-h-full max-w-full',
                  panelBox === null ? 'w-max' : 'w-full',
                )}
                /* The rows are not focusable and must not steal focus by being
               pressed: the trigger is the control, and `aria-activedescendant`
               only means anything while the trigger still has the keyboard. */
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
              >
                {options.map((option, index) => (
                  <div
                    key={option.value}
                    id={rowId(index)}
                    role="option"
                    aria-selected={option.value === value}
                    aria-disabled={option.disabled === true ? true : undefined}
                    data-active={index === active}
                    className={rowLook(
                      option.value === value,
                      index === active,
                      option.disabled === true,
                    )}
                    onClick={() => {
                      commit(index);
                    }}
                    /* `pointermove` and not `pointerenter`: a panel that scrolls
                   under a still pointer would otherwise re-highlight whatever
                   slid beneath it and fight the arrow keys. */
                    onPointerMove={() => {
                      if (index !== active) setActive(index);
                    }}
                  >
                    <span className="flex-1">{option.label}</span>
                    {/* The mark. Selection is the one thing in the panel that is
                    not a field, which is what lets a row be chosen and pointed
                    at in the same breath. `aria-selected` already said it; this
                    is for the eyes.

                    Bold, in the caret's 20-point box: the check itself comes
                    out about 16 wide, 11 tall and 1.9 thick, cap height beside
                    the row's 16-point label and one weight with the caret in
                    the field above. In a 12-point box it was 10 by 7 and 1.1
                    thick, which Mitch read as a little small on 2026-09-04;
                    the hand-drawn check before the icon set was 11 by 8 at a
                    2-point stroke in that same box. */}
                    {option.value === value && (
                      <CheckIcon
                        className="h-5 w-5 shrink-0"
                        aria-hidden="true"
                        focusable="false"
                        weight="bold"
                      />
                    )}
                  </div>
                ))}
              </div>
            </foreignObject>
          </svg>,
          document.body,
        )}
    </span>
  );
}
